import { useEffect, useState } from "react";
import { mediaUrl } from "../lib/api";

export const LogoMark = ({ className = "w-10 h-10", color = "#9A7B66" }) => (
  <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
    <g stroke={color} strokeWidth="6.5" strokeLinecap="round" fill="none">
      <path d="M50 54 L50 14" />
      <path d="M50 40 L34 28" />
      <path d="M50 40 L66 28" />
      <path d="M34 28 L28 14" />
      <path d="M34 28 L40 16" />
      <path d="M66 28 L60 16" />
      <path d="M66 28 L72 14" />
      <path d="M50 54 L50 90" />
      <path d="M50 68 L37 80" />
      <path d="M50 68 L63 80" />
      <path d="M37 80 L33 91" />
      <path d="M37 80 L42 90" />
      <path d="M63 80 L58 90" />
      <path d="M63 80 L67 91" />
      <path d="M46 54 L12 54" />
      <path d="M34 54 L24 42" />
      <path d="M34 54 L24 66" />
      <path d="M24 42 L12 38" />
      <path d="M24 42 L14 48" />
      <path d="M24 66 L12 70" />
      <path d="M24 66 L14 60" />
      <path d="M54 54 L88 54" />
      <path d="M66 54 L76 42" />
      <path d="M66 54 L76 66" />
      <path d="M76 42 L88 38" />
      <path d="M76 42 L86 48" />
      <path d="M76 66 L88 70" />
      <path d="M76 66 L86 60" />
    </g>
    <circle cx="50" cy="54" r="4.5" fill={color} />
  </svg>
);

export const Wordmark = ({ dark = false }) => (
  <span
    className={`font-sans font-bold tracking-[0.3em] text-sm sm:text-lg leading-none ${
      dark ? "text-[#9A7B66]" : "text-[#D8CBB8]"
    }`}
  >
    NUSADIRA
  </span>
);

export const Logo = ({ dark = false, markColor, logoUrl, logoLightUrl, height = 34 }) => {
  const effective = !dark && logoLightUrl ? logoLightUrl : logoUrl;
  const brandColor = dark ? "#9A7B66" : "#FFFFFF";

  // Guard: JPEG files do not have alpha transparency and turn CSS mask-image into a solid rectangle (white block).
  // Only use custom mask if the file is a transparent format (PNG / SVG / WEBP).
  const isJpg = effective && (effective.toLowerCase().includes(".jpg") || effective.toLowerCase().includes(".jpeg"));
  const hasValidCustomLogo = effective && !isJpg;

  // Auto-adjust: measure the uploaded logo's real aspect ratio so any file renders proportionally
  const [aspect, setAspect] = useState(null);
  useEffect(() => {
    if (!hasValidCustomLogo) return undefined;
    let alive = true;
    const probe = new window.Image();
    probe.onload = () => {
      if (alive && probe.naturalHeight) setAspect(probe.naturalWidth / probe.naturalHeight);
    };
    probe.src = mediaUrl(effective);
    return () => { alive = false; };
  }, [effective, hasValidCustomLogo]);

  // Same full logo on every device; height scales down automatically on small screens
  const mobileH = Math.max(22, Math.round(height * 0.72));
  const brandMaskStyle = hasValidCustomLogo
    ? {
        backgroundColor: brandColor,
        WebkitMaskImage: `url(${mediaUrl(effective)})`,
        maskImage: `url(${mediaUrl(effective)})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "left center",
        maskPosition: "left center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        height: `clamp(${mobileH}px, 6vw, ${height}px)`,
        aspectRatio: String(aspect || 6.6),
        maxWidth: "58vw",
      }
    : undefined;

  if (hasValidCustomLogo) {
    return (
      <span
        role="img"
        aria-label="Nusadira Lembongan Villa"
        className="block"
        style={brandMaskStyle}
        data-testid="custom-logo-image"
      />
    );
  }

  return (
    <span className="inline-flex items-center gap-2 sm:gap-3">
      <LogoMark className="w-7 h-7 sm:w-10 sm:h-10" color={markColor || (dark ? "#9A7B66" : "#D8CBB8")} />
      <Wordmark dark={dark} />
    </span>
  );
};
